import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppShell } from './components/layout/AppShell'
import { ProtectedRoute, GuestRoute } from './providers/RouteGuards'
import { LegacyRedirect } from './routes/LegacyRedirect'
import { RootErrorBoundary, SectionErrorBoundary } from './routes/RouteErrorBoundary'
import { Spinner } from './components/ui/primitives'

/**
 * Весь роутинг одним файлом — замена семи _layout.tsx мобильного приложения.
 * lazy() на каждую ветку: пользователь, зашедший почитать Библию, не должен
 * скачивать код групповых чатов.
 *
 * Домен делится на три зоны:
 *   «/»        — лендинг, публичный, индексируется поисковиками;
 *   «/legal/*» — юридические тексты, публичные: на них ссылаются и лендинг,
 *                и форма регистрации, а оферта обязана открываться без входа;
 *   «/lk/*»    — личный кабинет целиком, включая вход и регистрацию.
 */

const LoginPage = lazy(() => import('./routes/auth/LoginPage'))
const RegisterPage = lazy(() => import('./routes/auth/RegisterPage'))
const VerifyCodePage = lazy(() => import('./routes/auth/VerifyCodePage'))
const ForgotPasswordPage = lazy(() => import('./routes/auth/ForgotPasswordPage'))
const GreetingsPage = lazy(() => import('./routes/GreetingsPage'))
const ReferralLinkPage = lazy(() => import('./routes/ReferralLinkPage'))

const TodayPage = lazy(() => import('./routes/app/TodayPage'))
const PlanCatalogPage = lazy(() => import('./routes/plans/PlanCatalogPage'))
const PlanPage = lazy(() => import('./routes/plans/PlanPage'))
const UserPlanPage = lazy(() => import('./routes/plans/UserPlanPage'))
const PlanDayPage = lazy(() => import('./routes/plans/PlanDayPage'))
const BibleBooksPage = lazy(() => import('./routes/bible/BibleBooksPage'))
const BibleChaptersPage = lazy(() => import('./routes/bible/BibleChaptersPage'))
const BibleReaderPage = lazy(() => import('./routes/bible/BibleReaderPage'))
const BibleSearchPage = lazy(() => import('./routes/bible/BibleSearchPage'))

const ConversationsPage = lazy(() => import('./routes/chat/ConversationsPage'))
const ChatPage = lazy(() => import('./routes/chat/ChatPage'))
const NewGroupPage = lazy(() => import('./routes/chat/NewGroupPage'))
const GroupChatPage = lazy(() => import('./routes/chat/GroupChatPage'))
const GroupSettingsPage = lazy(() => import('./routes/chat/GroupSettingsPage'))

const NotesPage = lazy(() => import('./routes/notes/NotesPage'))
const NoteDetailPage = lazy(() => import('./routes/notes/NoteDetailPage'))
const NoteEditorPage = lazy(() => import('./routes/notes/NoteEditorPage'))

const ProfilePage = lazy(() => import('./routes/profile/ProfilePage'))
const PlansPage = lazy(() => import('./routes/profile/PlansPage'))
const PaymentResultPage = lazy(() => import('./routes/profile/PaymentResultPage'))
const AppPaymentReturnPage = lazy(() => import('./routes/app/AppPaymentReturnPage'))

const LandingPage = lazy(() => import('./routes/landing/LandingPage'))

const PrivacyPage = lazy(() => import('./routes/legal/PrivacyPage'))
const TermsPage = lazy(() => import('./routes/legal/TermsPage'))
const OfferPage = lazy(() => import('./routes/legal/OfferPage'))
const PersonalDataPage = lazy(() => import('./routes/legal/PersonalDataPage'))
const ConsentPage = lazy(() => import('./routes/legal/ConsentPage'))
const RefundPage = lazy(() => import('./routes/legal/RefundPage'))
const CookiesPage = lazy(() => import('./routes/legal/CookiesPage'))
const CommunityPage = lazy(() => import('./routes/legal/CommunityPage'))
const RequisitesPage = lazy(() => import('./routes/legal/RequisitesPage'))
const DeleteAccountPage = lazy(() => import('./routes/legal/DeleteAccountPage'))
const PartnerTermsPage = lazy(() => import('./routes/legal/PartnerTermsPage'))
const RegistrationApprovalPage = lazy(() => import('./routes/legal/RegistrationApprovalPage'))
const NotFoundPage = lazy(() => import('./routes/NotFoundPage'))

function Loading(): ReactNode {
  return (
    <div className="flex h-full min-h-[50vh] items-center justify-center">
      <Spinner size={32} className="text-primary" />
    </div>
  )
}

function withSuspense(element: ReactNode): ReactNode {
  return <Suspense fallback={<Loading />}>{element}</Suspense>
}

/** Корни, по которым кабинет был доступен до переезда под «/lk». */
const LEGACY_ROOTS = ['bible', 'chat', 'notes', 'profile', 'auth']

export const router = createBrowserRouter([
  {
    /*
     * Корневой errorElement — последний рубеж. Он ловит то, что осталось
     * без ближайшего errorElement: лендинг, юридические страницы, вход
     * и регистрацию. Кабинет накрыт своим, SectionErrorBoundary — см. ниже.
     *
     * Без хотя бы одного errorElement на дереве упавший динамический
     * import() (например, ленивый чанк раздела, за которым не заходили
     * при сети) рисует дефолтный экран React Router для разработчиков
     * прямо поверх приложения — с текстом ошибки и без единой кнопки.
     */
    errorElement: <RootErrorBoundary />,
    children: [
      { path: '/', element: withSuspense(<LandingPage />) },

      // Юридические тексты доступны без входа: на них ссылаются лендинг,
      // футер и форма регистрации
      { path: '/legal/privacy', element: withSuspense(<PrivacyPage />) },
      { path: '/legal/terms', element: withSuspense(<TermsPage />) },
      { path: '/legal/offer', element: withSuspense(<OfferPage />) },
      { path: '/legal/personal-data', element: withSuspense(<PersonalDataPage />) },
      { path: '/legal/consent', element: withSuspense(<ConsentPage />) },
      { path: '/legal/refund', element: withSuspense(<RefundPage />) },
      { path: '/legal/cookies', element: withSuspense(<CookiesPage />) },
      { path: '/legal/community', element: withSuspense(<CommunityPage />) },
      { path: '/legal/requisites', element: withSuspense(<RequisitesPage />) },
      { path: '/legal/delete-account', element: withSuspense(<DeleteAccountPage />) },
      // Условия для партнёров: в общий перечень документов не входят, но открываются
      // без входа — оферту принимают в кабинете партнёра, а прочитать её нужно до того
      { path: '/legal/partners', element: withSuspense(<PartnerTermsPage />) },
      { path: '/legal/registration', element: withSuspense(<RegistrationApprovalPage />) },

      // Мостик из ЮKassa обратно в мобильное приложение. Публичный: внутри
      // встроенного браузера приложения сессии сайта нет, и гард отправил бы
      // человека на форму входа вместо возврата в приложение.
      { path: '/app/payment-result', element: withSuspense(<AppPaymentReturnPage />) },

      // Ссылка приглашения: запоминает код друга и уводит на регистрацию
      { path: '/r/:code', element: withSuspense(<ReferralLinkPage />) },

      // Совместимость со старыми ссылками (кабинет жил в корне до лендинга)
      ...LEGACY_ROOTS.map((root) => ({ path: `/${root}/*`, element: <LegacyRedirect /> })),
      ...LEGACY_ROOTS.map((root) => ({ path: `/${root}`, element: <LegacyRedirect /> })),
      { path: '/greetings', element: <LegacyRedirect /> },
      { path: '/payment-result', element: <LegacyRedirect /> },

      {
        element: <GuestRoute />,
        children: [
          { path: '/lk/auth/login', element: withSuspense(<LoginPage />) },
          { path: '/lk/auth/register', element: withSuspense(<RegisterPage />) },
          { path: '/lk/auth/verify-code', element: withSuspense(<VerifyCodePage />) },
          { path: '/lk/auth/forgot-password', element: withSuspense(<ForgotPasswordPage />) },
        ],
      },

      {
        element: <ProtectedRoute />,
        children: [
          // Онбординг вне оболочки: таб-бар до заполнения профиля не нужен
          { path: '/lk/greetings', element: withSuspense(<GreetingsPage />) },
          { path: '/lk/payment-result', element: withSuspense(<PaymentResultPage />) },

          {
            element: <AppShell />,
            children: [
              {
                /*
                 * Раздельный узел ради errorElement: он должен подменить
                 * только контент внутри AppShell (Outlet), а не саму
                 * оболочку — иначе упавший чат заодно унёс бы таб-бар,
                 * которым из этого чата и надо было бы выйти.
                 */
                errorElement: <SectionErrorBoundary />,
                children: [
                  // Индексный маршрут здесь означал бы «/»: оболочка и гард — слои
                  // без пути. Корень занят лендингом, поэтому вход в кабинет явный.
                  { path: '/lk', element: <Navigate to="/lk/today" replace /> },
                  { path: '/lk/today', element: withSuspense(<TodayPage />) },

                  // Планы чтения: каталог общий, «me» — подписки человека
                  { path: '/lk/plans', element: withSuspense(<PlanCatalogPage />) },
                  { path: '/lk/plans/me/:id', element: withSuspense(<UserPlanPage />) },
                  { path: '/lk/plans/me/:id/day', element: withSuspense(<PlanDayPage />) },
                  { path: '/lk/plans/:slug', element: withSuspense(<PlanPage />) },

                  // Библия адресуется slug'ом — ссылку на главу можно отправить
                  { path: '/lk/bible', element: <Navigate to="/lk/bible/syn" replace /> },
                  { path: '/lk/bible/search', element: withSuspense(<BibleSearchPage />) },
                  { path: '/lk/bible/:translation', element: withSuspense(<BibleBooksPage />) },
                  {
                    path: '/lk/bible/:translation/:book',
                    element: withSuspense(<BibleChaptersPage />),
                  },
                  {
                    path: '/lk/bible/:translation/:book/:chapter',
                    element: withSuspense(<BibleReaderPage />),
                  },

                  { path: '/lk/chat', element: withSuspense(<ConversationsPage />) },
                  { path: '/lk/chat/new-group', element: withSuspense(<NewGroupPage />) },
                  { path: '/lk/chat/group/:id', element: withSuspense(<GroupChatPage />) },
                  {
                    path: '/lk/chat/group/:id/settings',
                    element: withSuspense(<GroupSettingsPage />),
                  },
                  { path: '/lk/chat/:id', element: withSuspense(<ChatPage />) },

                  { path: '/lk/notes', element: withSuspense(<NotesPage />) },
                  { path: '/lk/notes/editor', element: withSuspense(<NoteEditorPage />) },
                  { path: '/lk/notes/:id', element: withSuspense(<NoteDetailPage />) },

                  { path: '/lk/profile', element: withSuspense(<ProfilePage />) },
                  { path: '/lk/profile/plans', element: withSuspense(<PlansPage />) },
                ],
              },
            ],
          },
        ],
      },

      { path: '*', element: withSuspense(<NotFoundPage />) },
    ],
  },
])
